import { Badge } from "@/components/ui/badge";
import { Clock, AlertTriangle, CheckCircle2 } from "lucide-react";

const STATUS_STYLES = {
  lead: "bg-slate-100 text-slate-700 border-slate-300",
  onboarded: "bg-sky-50 text-sky-800 border-sky-300",
  active: "bg-blue-50 text-blue-800 border-blue-300",
  resolved: "bg-emerald-50 text-emerald-800 border-emerald-300",
  drafted: "bg-slate-100 text-slate-700 border-slate-300",
  mailed: "bg-blue-50 text-blue-800 border-blue-300",
  in_review: "bg-amber-50 text-amber-800 border-amber-300",
  responded: "bg-cyan-50 text-cyan-800 border-cyan-300",
  flagged: "bg-amber-50 text-amber-800 border-amber-300",
  confirmed: "bg-blue-50 text-blue-800 border-blue-300",
  dismissed: "bg-slate-100 text-slate-500 border-slate-200",
  disputed: "bg-indigo-50 text-indigo-800 border-indigo-300",
  paid: "bg-emerald-50 text-emerald-800 border-emerald-300",
  pending: "bg-amber-50 text-amber-800 border-amber-300",
  void: "bg-slate-100 text-slate-500 border-slate-200",
  signed: "bg-emerald-50 text-emerald-800 border-emerald-300",
  sent: "bg-blue-50 text-blue-800 border-blue-300",
};

const LABELS = {
  lead: "Lead", onboarded: "Onboarded", active: "Active", resolved: "Resolved",
  drafted: "Drafted", mailed: "Mailed", in_review: "In Bureau Review", responded: "Responded",
  flagged: "AI Flagged", confirmed: "Confirmed", dismissed: "Dismissed", disputed: "In Dispute",
  paid: "Paid", pending: "Pending", void: "Void", signed: "Signed", sent: "Sent",
};

export function StatusChip({ status, testId }) {
  const s = status || "lead";
  return (
    <Badge variant="outline" data-testid={testId || `status-chip-${s}`}
      className={`${STATUS_STYLES[s] || "bg-slate-100 text-slate-700 border-slate-300"} font-medium text-xs whitespace-nowrap`}>
      {LABELS[s] || s}
    </Badge>
  );
}

const BUREAU_STYLES = {
  experian: "bg-blue-700 text-white",
  transunion: "bg-sky-600 text-white",
  equifax: "bg-red-700 text-white",
  unknown: "bg-slate-500 text-white",
};

export function BureauChip({ bureau }) {
  const b = (bureau || "unknown").toLowerCase();
  const label = b === "unknown" ? "Unknown" : b.charAt(0).toUpperCase() + b.slice(1);
  return (
    <span data-testid={`bureau-chip-${b}`}
      className={`${BUREAU_STYLES[b] || BUREAU_STYLES.unknown} inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-semibold tracking-wide`}>
      {label}
    </span>
  );
}

export function TimerBadge({ daysLeft, status }) {
  if (!["mailed", "in_review"].includes(status) || daysLeft === null || daysLeft === undefined) {
    return <span className="text-xs text-slate-400">—</span>;
  }
  if (daysLeft < 0) {
    return (
      <span data-testid="timer-30day-badge" className="inline-flex items-center gap-1 rounded bg-red-50 border border-red-300 px-2 py-0.5 text-xs font-semibold text-red-700">
        <AlertTriangle className="h-3 w-3" /> OVERDUE {Math.abs(daysLeft)}d
      </span>
    );
  }
  const tone = daysLeft <= 10
    ? "bg-amber-50 border-amber-300 text-amber-800"
    : "bg-emerald-50 border-emerald-300 text-emerald-800";
  return (
    <span data-testid="timer-30day-badge" className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-semibold ${tone}`}>
      <Clock className="h-3 w-3" /> {daysLeft}d left
    </span>
  );
}

export function OutcomeChip({ outcome }) {
  if (!outcome) return null;
  const map = {
    deleted: "bg-emerald-50 text-emerald-800 border-emerald-300",
    verified: "bg-red-50 text-red-700 border-red-300",
    updated: "bg-sky-50 text-sky-800 border-sky-300",
    mixed: "bg-amber-50 text-amber-800 border-amber-300",
    no_response: "bg-indigo-50 text-indigo-800 border-indigo-300",
  };
  return (
    <Badge variant="outline" className={`${map[outcome] || "bg-slate-100 text-slate-700"} text-xs font-medium`}>
      <CheckCircle2 className="h-3 w-3 mr-1" /> {outcome === "no_response" ? "No response → escalated" : outcome.charAt(0).toUpperCase() + outcome.slice(1)}
    </Badge>
  );
}
